import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AppBar,
  Box,
  Dialog,
  Grid,
  IconButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { format } from "date-fns";
import { intlFormatDate } from "helpers";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { updateChecklistSpec, updateInspection } from "store/slices/jobs.store";
import { ChecklistType } from "operations/schema/schema";
import { cloneDeep } from "lodash";
import { any } from "prop-types";
import PrimaryButton from "components/PrimaryButton";
import { ChecklistRenderer } from "../ChecklistRenderer";
type InspectionDialogProps = {
  open: boolean;
  handleClose: () => void;
};

export const InspectionDialog: FC<InspectionDialogProps> = (props) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { open, handleClose } = props;
  const { selectedInspection: inspection } = useAppSelector((s) => s.jobs);
  const [checklistStateSpec, setChecklistStateSpec] = useState<ChecklistType | null>(null);
  useEffect(() => {
    if (inspection?.specification.checklists ? [0] : any) {
      setChecklistStateSpec(cloneDeep(inspection?.specification?.checklists?.[0] ?? null));
    }
  }, [inspection?.specification.checklists]);

 const save = () => {
  if (!checklistStateSpec) return;
  dispatch(updateChecklistSpec({ checklist: checklistStateSpec }));
  handleClose(); 
};

  const checklists = inspection?.specification.checklists;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={Transition}
      data-testid="inspectionDialog"
    >
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => handleClose()}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="inspectionDialogCloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar.label" paddingRight="36px">
            <ListItemText
              primary={`${inspection?.specification.id} / ${inspection?.specification.description}`}
              sx={{ maxWidth: "80%" }}
            />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />

      <StyledContainer>
        <Grid container direction="column" spacing={2}>
          <Grid item container direction="row">
            <Grid item mr={1}>
              <Typography variant="body1" fontWeight={700}>
                <FormattedMessage id="inspection.description" />:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">{inspection?.specification.description}</Typography>
            </Grid>
          </Grid>
          <Grid item container direction="row">
            <Grid item mr={1}>
              <Typography variant="body1" fontWeight={700}>
                <FormattedMessage id="inspection.location" />:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">{inspection?.specification.location}</Typography>
            </Grid>
          </Grid>
          <Grid item container direction="row">
            <Grid item mr={1}>
              <Typography variant="body1" fontWeight={700}>
                <FormattedMessage id="inspection.quantity" />:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">{inspection?.specification.quantity}</Typography>
            </Grid>
          </Grid>
          <Grid item container direction="row">
            <Grid item mr={1}>
              <Typography variant="body1" fontWeight={700}>
                <FormattedMessage id="inspection.dateInstalled" />:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {inspection?.specification.dateInstalled
                  ? intlFormatDate(intl, inspection.specification.dateInstalled)
                  : "null"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Last Service: </span>
              <Box
                component="span"
                sx={{
                  fontWeight: "bold",
                  color:
                    inspection?.specification.resultOfLastService?.toLowerCase() === "pass"
                      ? "green"
                      : inspection?.specification.resultOfLastService?.toLowerCase() === "fail"
                      ? "red"
                      : "grey",
                }}
              >
                {inspection?.specification.resultOfLastService}
              </Box>
              {` on ${
                inspection?.specification.lastServiceDate
                  ? format(new Date(inspection.specification.lastServiceDate), "dd/MM/yyyy")
                  : "N/A"
              }`}
            </Typography>
          </Grid>
          <Grid item>
            <StyledTextField
              value={inspection?.notes || ""}
              label={intl.formatMessage({ id: "note" })}
              onChange={async (e) => {
                if (!inspection) return;
                dispatch(
                  updateInspection({
                    inspection: { ...inspection, notes: e.target.value },
                  })
                );
              }}
              multiline
              rows={15}
              data-testid="InspectionDialog-Text"
            />
          </Grid>
        </Grid>
      </StyledContainer>
      {checklists && checklistStateSpec && (
        <StyledContainer>
          <ChecklistRenderer
            checklist={checklistStateSpec}
            setChecklist={setChecklistStateSpec}
            onSave={save}
            renderSaveButton={
              <PrimaryButton
                fullWidth
                onClick={save}
                className="e2e-save-checklist-button"
                data-testid="ChecklistDialogSaveButton"
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            }
          />
        </StyledContainer>
      )}
    </Dialog>
  );
};
